/**
 * useFullCatalogEntry — the FULL catalog entry for `seed`, or nothing (plugin
 * catalog round, 2026-10-06).
 *
 * `entry` is never a compact entry. While a compact seed's full entry is read
 * it is `null` with status "loading"; a failed read is "error" (still `null`)
 * until `retry` reads again. A seed without the compact marker is already full
 * and comes back as-is, "ready", on the first render — nothing is read. `entry`
 * is a stable reference (the seed itself, or the cached object), so effects
 * keyed on it run once per workflow.
 */
import { useCallback, useEffect, useState } from "react";
import type { WorkflowCatalogEntry } from "../types/workflow";
import {
  isCompactCatalogEntry,
  loadFullCatalogEntry,
  peekFullCatalogEntry,
} from "../services/catalogEntryCache";

export interface FullCatalogEntry {
  /** The FULL entry, or null while none is in hand. Never a compact entry. */
  entry: WorkflowCatalogEntry | null;
  /** none: no workflow. loading: reading it. ready: `entry` is full. error: the read failed. */
  status: "none" | "loading" | "ready" | "error";
  /** Read again after an error. Stable. */
  retry: () => void;
}

export function useFullCatalogEntry(
  seed: WorkflowCatalogEntry | null | undefined,
): FullCatalogEntry {
  const workflowId = seed?.id || null;
  const needsRead = workflowId !== null && isCompactCatalogEntry(seed);
  const [attempt, setAttempt] = useState(0);
  const [failure, setFailure] = useState<{ workflowId: string; attempt: number } | null>(null);
  const [, setSettled] = useState(0);
  // A failure belongs to the load that produced it: when the workflow changes
  // (including to none) it is dropped during render, so a later return to the
  // same id never flashes "error" before "loading". React re-renders at once.
  const [seenId, setSeenId] = useState<string | null>(workflowId);
  if (seenId !== workflowId) {
    setSeenId(workflowId);
    setFailure(null);
  }
  const retry = useCallback(() => setAttempt((n) => n + 1), []);

  useEffect(() => {
    if (!needsRead || workflowId === null || peekFullCatalogEntry(workflowId)) return;
    let cancelled = false;
    setFailure(null);
    loadFullCatalogEntry(workflowId).then(
      () => {
        if (!cancelled) setSettled((n) => n + 1);
      },
      () => {
        if (!cancelled) setFailure({ workflowId, attempt });
      },
    );
    return () => {
      cancelled = true;
    };
  }, [needsRead, workflowId, attempt]);

  if (!seed || workflowId === null) return { entry: null, status: "none", retry };
  if (!needsRead) return { entry: seed, status: "ready", retry };
  const full = peekFullCatalogEntry(workflowId);
  if (full) return { entry: full, status: "ready", retry };
  if (failure && failure.workflowId === workflowId && failure.attempt === attempt) {
    return { entry: null, status: "error", retry };
  }
  return { entry: null, status: "loading", retry };
}
